"use strict";
/**
 * Pure helpers for the UseCaseAlignment feature.
 *
 * Includes presentation helpers (dropdown options, placeholder copy) and
 * defensive runtime validators that re-check API payloads at the trust
 * boundary — TypeScript cannot enforce that the wire format actually matches
 * the declared interface.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.validateAlignmentResponse = exports.sanitizeOpaqueId = exports.resolvePlaceholderText = exports.buildStrategyOptions = exports.ERROR_PLACEHOLDER_TEXT = exports.DEFAULT_PLACEHOLDER_TEXT = void 0;
/**
 * Default placeholder text shown when no alignment is available yet.
 */
exports.DEFAULT_PLACEHOLDER_TEXT = 'Select a strategy and use case to see the visualization. The graph will display how the strategy connects through business metrics to the selected use case.';
/**
 * Placeholder text shown after a fetch error (typically empty strategy).
 */
exports.ERROR_PLACEHOLDER_TEXT = 'The graph cannot be generated until the selected strategy has elements. First add elements in Strategy Builder, then try again.';
/**
 * Map strategies to Fabric Dropdown options.
 *
 * Accepts a structural subset of `Strategy` (just `id` + `name`) so the
 * surface area used here is documented in the type and a future rename of an
 * unrelated field on `Strategy` cannot silently break the dropdown.
 */
exports.buildStrategyOptions = function (strategies) {
    return strategies.map(function (strategy) { return ({
        key: String(strategy.id),
        text: strategy.name,
    }); });
};
/**
 * Resolve the placeholder copy based on whether the last attempt errored.
 */
exports.resolvePlaceholderText = function (hasError) {
    return hasError ? exports.ERROR_PLACEHOLDER_TEXT : exports.DEFAULT_PLACEHOLDER_TEXT;
};
/* ------------------------------------------------------------------------- *
 * Security-oriented runtime guards                                          *
 * ------------------------------------------------------------------------- */
/**
 * Allowed shape for an opaque ID arriving from the embedding host (typically
 * a URL query param or DOM attribute). Limits to URL-safe characters and a
 * sane upper bound to mitigate log injection, accidental template misuse, and
 * pathological server-side processing of attacker-controlled input.
 */
var ID_ALLOWED_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
/** Upper bound on rendered string fields (column / node names). */
var MAX_DISPLAY_STRING_LENGTH = 256;
/** Upper bound on collection sizes — protects the renderer from DOM bombs. */
var MAX_COLUMNS = 32;
var MAX_NODES_PER_COLUMN = 256;
var MAX_AFFECTED_NODES = MAX_COLUMNS * MAX_NODES_PER_COLUMN;
/**
 * Returns the input when it matches the safe ID pattern, otherwise an empty
 * string. The empty string is the hook's documented "no selection" sentinel,
 * so a malformed ID becomes a no-op rather than a silent forwarded request.
 */
exports.sanitizeOpaqueId = function (value) {
    if (typeof value !== 'string') {
        return '';
    }
    return ID_ALLOWED_PATTERN.test(value) ? value : '';
};
var isPlainObject = function (value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};
var isNonBlankBoundedString = function (value) {
    return typeof value === 'string' && value.trim().length > 0 && value.length <= MAX_DISPLAY_STRING_LENGTH;
};
var isStrategyNode = function (value) {
    if (!isPlainObject(value)) {
        return false;
    }
    if (!isNonBlankBoundedString(value.id)) {
        return false;
    }
    if (!isNonBlankBoundedString(value.name)) {
        return false;
    }
    return isPlainObject(value.edges);
};
var isStrategyColumn = function (value) {
    if (!isPlainObject(value)) {
        return false;
    }
    if (!isNonBlankBoundedString(value.id)) {
        return false;
    }
    if (!isNonBlankBoundedString(value.name)) {
        return false;
    }
    if (!Array.isArray(value.nodes)) {
        return false;
    }
    if (value.nodes.length > MAX_NODES_PER_COLUMN) {
        return false;
    }
    return value.nodes.every(isStrategyNode);
};
var isAffectedNodeScore = function (value) {
    if (!isPlainObject(value)) {
        return false;
    }
    if (!isNonBlankBoundedString(value.nodeId)) {
        return false;
    }
    return typeof value.score === 'number' && Number.isFinite(value.score);
};
/**
 * Validate a parsed alignment response at the trust boundary.
 *
 * Rejects:
 *  - non-objects / arrays at the root
 *  - missing or non-array `structure`
 *  - empty `structure` (a renderable graph requires at least one column)
 *  - columns / nodes with blank, missing, or oversized identifiers / names
 *  - collection sizes beyond hardened upper bounds (DOM-bomb defense)
 *
 * @returns the same value re-typed when valid; `null` when malformed.
 */
exports.validateAlignmentResponse = function (value) {
    if (!isPlainObject(value)) {
        return null;
    }
    if (!Array.isArray(value.structure)) {
        return null;
    }
    if (value.structure.length === 0 || value.structure.length > MAX_COLUMNS) {
        return null;
    }
    if (!value.structure.every(isStrategyColumn)) {
        return null;
    }
    if (!Array.isArray(value.affectedNodes)) {
        return null;
    }
    if (value.affectedNodes.length > MAX_AFFECTED_NODES) {
        return null;
    }
    if (!value.affectedNodes.every(isAffectedNodeScore)) {
        return null;
    }
    // Safe cast: `structure` and `affectedNodes` were narrowed by the guards
    // above. Routed through `unknown` to satisfy strict-mode overlap rules.
    return value;
};
